import type { OdooTask, FaseSysteem } from '@/types/odoo';
import { displayProjectName } from '@/utils/format';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function SysteemCell({ entry }: { entry?: FaseSysteem }) {
  if (!entry || entry.systemMix.length === 0) {
    return <span className="text-sm text-td-light italic">-</span>;
  }
  const mismatchTitle = entry.mismatches
    .map((m) => `${m.name}: ${cap(m.declared)} ≠ materiaallijst (${m.detected.map(cap).join(' / ')})`)
    .join('\n');

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {entry.systemMix.map((s) => (
          <span key={s.systeem} className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-td-text rounded-full">
            {cap(s.systeem)} {Math.round(s.m2)}m²
          </span>
        ))}
      </div>
      {entry.mismatches.length > 0 && (
        <span
          title={mismatchTitle}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-orange-50 text-orange-600 rounded-full cursor-help"
        >
          ⚠ Systeem ≠ materiaallijst
        </span>
      )}
    </div>
  );
}

/** "Uitgevoerde m² deze fase": op-druk collector m² over the fase's systeem total. */
function UitgevoerdM2Cell({ entry }: { entry?: FaseSysteem }) {
  if (!entry || entry.totalM2 === 0) {
    return <span className="text-sm text-td-light italic">-</span>;
  }

  const { opDrukM2, totalM2 } = entry;
  let colorClasses: string;

  if (opDrukM2 >= totalM2) {
    colorClasses = 'bg-td-green/10 text-td-green';
  } else if (opDrukM2 > 0) {
    colorClasses = 'bg-teal-50 text-teal-600';
  } else {
    colorClasses = 'bg-gray-100 text-td-muted';
  }

  return (
    <span className={`px-2.5 py-1 text-xs font-bold ${colorClasses} rounded-full whitespace-nowrap`}>
      {Math.round(opDrukM2)} / {Math.round(totalM2)} m²
    </span>
  );
}

function InstallerCell({ names }: { names: string[] }) {
  if (names.length === 0) {
    return <span className="text-sm text-td-light italic">Niet toegewezen</span>;
  }

  return (
    <div className="space-y-0.5">
      {names.map((name, i) => (
        <div key={i} className="text-sm text-td-text">{name}</div>
      ))}
    </div>
  );
}

function getProjectleiderRowBg(task: OdooTask): string {
  const plName = task.x_studio_projectleider ? task.x_studio_projectleider[1].toLowerCase() : '';
  if (plName.includes('david')) return 'bg-blue-50';
  if (plName.includes('jorn')) return 'bg-gray-100';
  return '';
}

function OpgelijstBadge({ value }: { value?: boolean }) {
  if (value) {
    return (
      <span className="px-2.5 py-1 text-xs font-bold bg-td-green/10 text-td-green rounded-full">
        Opgelijst
      </span>
    );
  }
  return (
    <span className="px-2.5 py-1 text-xs font-bold bg-orange-50 text-orange-500 rounded-full">
      Niet opgelijst
    </span>
  );
}

interface DataTableProps {
  tasks: OdooTask[];
  installerNames: Record<number, string>;
  projectNames: Record<number, string>;
  werfAddresses: Record<number, string>;
  faseSystems?: Record<number, FaseSysteem>;
  onTaskClick: (task: OdooTask) => void;
  showOpgelijst?: boolean;
  showFotos?: boolean;
  showSysteem?: boolean;
}

export function DataTable({ tasks, installerNames, projectNames, werfAddresses, faseSystems, onTaskClick, showOpgelijst, showFotos = true, showSysteem }: DataTableProps) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
              Project
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
              Taak
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
              Werf
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
              Installateur
            </th>
            {showSysteem && (
              <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
                Systeem
              </th>
            )}
            {showOpgelijst && (
              <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
                Opgelijst
              </th>
            )}
            {showFotos && (
              <th className="text-left px-5 py-3.5 text-xs font-bold text-td-light uppercase tracking-wider">
                Uitgevoerde m&sup2;
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {tasks.map((task) => {
            const soId = task.sale_order_id && Array.isArray(task.sale_order_id) ? task.sale_order_id[0] : null;
            const projectName = (soId && projectNames[soId]) || (task.project_id ? displayProjectName(task.project_id[1]) : '');
            const werfId = Array.isArray(task.x_studio_afleveradres) ? task.x_studio_afleveradres[0] : null;
            const address = werfId ? werfAddresses[werfId] || '' : '';
            const instIds: number[] = Array.isArray(task.x_studio_installateur) ? task.x_studio_installateur : [];
            const instNames = instIds.map(id => installerNames[id]).filter(Boolean);

            return (
              <tr
                key={task.id}
                onClick={() => onTaskClick(task)}
                className={`${getProjectleiderRowBg(task)} hover:bg-td-magenta/[0.03] cursor-pointer transition group`}
              >
                <td className="px-5 py-5">
                  {projectName ? (
                    <span className="text-sm text-td-text">{projectName}</span>
                  ) : (
                    <span className="text-sm text-td-light italic">-</span>
                  )}
                </td>
                <td className="px-5 py-5">
                  <span className="text-sm font-bold text-td-blue group-hover:text-td-magenta group-hover:underline transition">
                    {task.name}
                  </span>
                  {task.isInterventie && <InterventieBadge className="ml-2 align-middle" />}
                </td>
                <td className="px-5 py-5 text-sm text-td-muted">
                  {address || <span className="text-td-light italic">-</span>}
                </td>
                <td className="px-5 py-5">
                  <InstallerCell names={instNames} />
                </td>
                {showSysteem && (
                  <td className="px-5 py-5">
                    <SysteemCell entry={faseSystems?.[task.id]} />
                  </td>
                )}
                {showOpgelijst && (
                  <td className="px-5 py-5">
                    <OpgelijstBadge value={task.x_studio_opgelijst} />
                  </td>
                )}
                {showFotos && (
                  <td className="px-5 py-5">
                    <UitgevoerdM2Cell entry={faseSystems?.[task.id]} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
